import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface Pipeline {
  id: string;
  company_id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PipelineStatus {
  id: string;
  pipeline_id: string;
  company_id: string;
  name: string;
  position: number;
  color: string;
  is_terminal: boolean;
  created_at: string;
  updated_at: string;
}

// -----------------------------------------------------------------------
// Queries
// -----------------------------------------------------------------------

export function usePipelines() {
  return useQuery({
    queryKey: ['pipelines'],
    queryFn: async (): Promise<Pipeline[]> => {
      const { data, error } = await supabase
        .from('company_pipelines')
        .select('id, company_id, name, is_active, created_at, updated_at')
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60 * 1000,
  });
}

export function usePipelineStatuses(pipelineId: string | null) {
  return useQuery({
    queryKey: ['pipeline-statuses', pipelineId],
    queryFn: async (): Promise<PipelineStatus[]> => {
      if (!pipelineId) return [];
      const { data, error } = await supabase
        .from('pipeline_statuses')
        .select('id, pipeline_id, company_id, name, position, color, is_terminal, created_at, updated_at')
        .eq('pipeline_id', pipelineId)
        .order('position', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!pipelineId,
    staleTime: 60 * 1000,
  });
}

// -----------------------------------------------------------------------
// Pipeline mutations
// -----------------------------------------------------------------------

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from('company_pipelines')
        .insert({ name })
        .select()
        .single();
      if (error) throw error;
      return data as Pipeline;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
      toast({ title: 'Pipeline created' });
    },
    onError: (e: Error) => toast({ title: 'Failed to create pipeline', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase
        .from('company_pipelines')
        .update({ name })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
      toast({ title: 'Pipeline updated' });
    },
    onError: (e: Error) => toast({ title: 'Failed to update pipeline', description: e.message, variant: 'destructive' }),
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Hard delete. Stages cascade, and since migration 20261001100000 so do
      // the projects in the pipeline (their job links go with them; jobs stay).
      const { error } = await supabase
        .from('company_pipelines')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
      qc.invalidateQueries({ queryKey: ['pipeline-statuses'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast({ title: 'Pipeline deleted' });
    },
    onError: (e: Error) => toast({ title: 'Failed to delete pipeline', description: e.message, variant: 'destructive' }),
  });
}

// -----------------------------------------------------------------------
// Pipeline status mutations
// -----------------------------------------------------------------------

export function useCreatePipelineStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pipelineId,
      name,
      color = '#3b82f6',
      position,
    }: {
      pipelineId: string;
      name: string;
      color?: string;
      position: number;
    }) => {
      const { data, error } = await supabase
        .from('pipeline_statuses')
        .insert({ pipeline_id: pipelineId, name, color, position })
        .select()
        .single();
      if (error) throw error;
      return data as PipelineStatus;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-statuses', vars.pipelineId] });
      toast({ title: 'Status added' });
    },
    onError: (e: Error) => toast({ title: 'Failed to add status', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdatePipelineStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      pipelineId,
      updates,
    }: {
      id: string;
      pipelineId: string;
      updates: Partial<Pick<PipelineStatus, 'name' | 'color' | 'is_terminal' | 'position'>>;
    }) => {
      const { error } = await supabase
        .from('pipeline_statuses')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-statuses', vars.pipelineId] });
      toast({ title: 'Stage updated' });
    },
    onError: (e: Error) => toast({ title: 'Failed to update status', description: e.message, variant: 'destructive' }),
  });
}

export function useDeletePipelineStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, pipelineId }: { id: string; pipelineId: string }) => {
      const { error } = await supabase
        .from('pipeline_statuses')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-statuses', vars.pipelineId] });
      toast({ title: 'Status removed' });
    },
    onError: (e: Error) => toast({ title: 'Failed to remove status', description: e.message, variant: 'destructive' }),
  });
}

export function useReorderPipelineStatuses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pipelineId,
      orderedIds,
    }: {
      pipelineId: string;
      orderedIds: string[];
    }) => {
      // Update each status position in parallel
      const updates = orderedIds.map((id, index) =>
        supabase.from('pipeline_statuses').update({ position: index }).eq('id', id)
      );
      const results = await Promise.all(updates);
      const err = results.find(r => r.error)?.error;
      if (err) throw err;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['pipeline-statuses', vars.pipelineId] });
    },
    onError: (e: Error) => toast({ title: 'Failed to reorder statuses', description: e.message, variant: 'destructive' }),
  });
}

// -----------------------------------------------------------------------
// Default pipeline seeding
// -----------------------------------------------------------------------

export function useSeedDefaultPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // A previous attempt may have created the pipeline and then failed on the
      // stages (the pipeline insert is not in the same transaction). Reuse an
      // active pipeline that has no stages rather than adding a second one.
      const { data: existing, error: eErr } = await supabase
        .from('company_pipelines')
        .select('id, company_id, name, is_active, created_at, updated_at, pipeline_statuses(id)')
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      if (eErr) throw eErr;
      const stageless = (existing ?? []).find(
        p => !((p as unknown as { pipeline_statuses?: unknown[] }).pipeline_statuses?.length),
      );

      let pipeline: Pipeline;
      let createdHere = false;
      if (stageless) {
        pipeline = stageless as unknown as Pipeline;
      } else {
        const { data: created, error: pErr } = await supabase
          .from('company_pipelines')
          .insert({ name: 'Default Pipeline' })
          .select()
          .single();
        if (pErr) throw pErr;
        pipeline = created as Pipeline;
        createdHere = true;
      }

      // Every row carries every column. The client sends one column list for
      // the whole batch, so a key missing from some rows is written as NULL,
      // not as the column default — is_terminal is NOT NULL and failed that way.
      const defaultStatuses = [
        { name: 'Deposit Taken', color: '#f59e0b', position: 0, is_terminal: false },
        { name: 'Parts Ordered',  color: '#3b82f6', position: 1, is_terminal: false },
        { name: 'Parts Arrived',  color: '#8b5cf6', position: 2, is_terminal: false },
        { name: 'Scheduled',      color: '#06b6d4', position: 3, is_terminal: false },
        { name: 'Complete',       color: '#10b981', position: 4, is_terminal: true },
      ];

      const { error: sErr } = await supabase
        .from('pipeline_statuses')
        .insert(defaultStatuses.map(s => ({ ...s, pipeline_id: pipeline.id })));
      if (sErr) {
        // Don't leave an empty pipeline behind for the next attempt to trip over.
        if (createdHere) await supabase.from('company_pipelines').delete().eq('id', pipeline.id);
        throw sErr;
      }

      return pipeline;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
      qc.invalidateQueries({ queryKey: ['pipeline-statuses'] });
      toast({ title: 'Default pipeline created' });
    },
    onError: (e: Error) => toast({ title: 'Failed to seed pipeline', description: e.message, variant: 'destructive' }),
  });
}
